import { useState } from 'react';
import { supabase } from '../config/supabase';
import { Ticket, User } from '../types';

interface UseTicketOperationsProps {
    currentUser: User | null;
    users: User[];
    onNotify: (type: 'success' | 'error' | 'warning' | 'info', message: string) => void;
    onReload: () => Promise<void>;
}

export const useTicketOperations = ({
    currentUser,
    users,
    onNotify,
    onReload
}: UseTicketOperationsProps) => {
    const [isLoading, setIsLoading] = useState(false);

    /**
     * Create a new ticket
     */
    const createTicket = async (data: any): Promise<boolean> => {
        if (!currentUser) {
            onNotify('error', 'No hay usuario autenticado');
            return false;
        }

        try {
            setIsLoading(true);

            const slaHours = data.priority === 'P1' ? 2 : data.priority === 'P2' ? 4 : 24;
            const slaDue = new Date(Date.now() + (slaHours * 3600000));

            // Validate User ID - Fix for "invalid input syntax for type uuid"
            let creatorId = currentUser.id;
            const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

            if (!uuidRegex.test(creatorId)) {
                console.warn('⚠️ Legacy User ID detected:', creatorId);

                // Try matching by Name or Email
                let realUser = users.find(u => u.email === currentUser.email || u.name === currentUser.name);

                // If not found and is Store, match by Site ID
                if (!realUser && currentUser.role === 'STORE' && currentUser.siteId) {
                    realUser = users.find(u => u.siteId === currentUser.siteId && u.role === 'STORE');
                }

                if (realUser) {
                    console.log('✅ Found real UUID:', realUser.id);
                    creatorId = realUser.id;
                } else {
                    console.error('❌ Could not find real UUID for user');
                    onNotify('error', 'Error de sesión: Usuario no sincronizado. Favor cerrar sesión e ingresar nuevamente.');
                    return false;
                }
            }

            // Insert into Supabase
            const { data: newTicketData, error } = await supabase
                .from('tickets')
                .insert({
                    site_id: data.siteId,
                    site_name: data.siteName,
                    created_by: creatorId,
                    type: data.type,
                    category: data.category,
                    subcategory: data.subcategory,
                    priority: data.priority,
                    status: 'OPEN',
                    description: data.description,
                    impact: data.impact,
                    urgency: data.urgency,
                    sla_due: slaDue.toISOString(),
                })
                .select()
                .single();

            if (error) throw error;

            // Add timeline event
            await supabase.from('timeline_events').insert({
                ticket_id: newTicketData.id,
                user_id: creatorId,
                user_name: currentUser.name,
                event_type: 'SYSTEM',
                content: `Ticket creado vía Portal Web - Prioridad ${data.priority}`,
            });

            // Reload tickets
            await onReload();

            onNotify('success', `Ticket ${newTicketData.ticket_number || newTicketData.id} creado exitosamente`);
            return true;
        } catch (error: any) {
            console.error('Error creating ticket:', error);
            onNotify('error', 'Error al crear ticket: ' + error.message);
            return false;
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Update an existing ticket
     */
    const updateTicket = async (updatedTicket: Ticket): Promise<boolean> => {
        try {
            setIsLoading(true);

            const { error } = await supabase
                .from('tickets')
                .update({
                    status: updatedTicket.status,
                    assigned_to: updatedTicket.assignedTo,
                    assigned_to_name: updatedTicket.assignedToName,
                    description: updatedTicket.description,
                })
                .eq('id', updatedTicket.id);

            if (error) throw error;

            await onReload();
            onNotify('success', 'Ticket actualizado');
            return true;
        } catch (error: any) {
            console.error('Error updating ticket:', error);
            onNotify('error', 'Error al actualizar ticket: ' + error.message);
            return false;
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Delete a ticket (Hard Delete)
     * Warning: This removes data permanently
     */
    const deleteTicket = async (ticketId: string): Promise<boolean> => {
        if (!window.confirm('¿ELIMINAR DEFINITIVAMENTE? Esta acción borrará el ticket y todo su historial para siempre. ¿Desea continuar?')) {
            return false;
        }

        try {
            setIsLoading(true);

            const { error } = await supabase
                .from('tickets')
                .delete()
                .eq('id', ticketId);

            if (error) throw error;

            await onReload();
            onNotify('success', 'Ticket eliminado permanentemente');
            return true;
        } catch (error: any) {
            console.error('Error deleting ticket:', error);
            onNotify('error', 'Error al eliminar ticket: ' + error.message);
            return false;
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Archive/Close a ticket (Soft Delete)
     * Moves ticket to history by setting status to CLOSED
     */
    const archiveTicket = async (ticketId: string): Promise<boolean> => {
        if (!window.confirm('¿Cerrar y archivar este ticket? Se moverá al historial.')) {
            return false;
        }

        try {
            setIsLoading(true);

            const { error } = await supabase
                .from('tickets')
                .update({ status: 'CLOSED' })
                .eq('id', ticketId);

            if (error) throw error;

            // Add system comment
            await addComment(ticketId, 'Ticket cerrado y archivado manualmente por usuario');

            await onReload();
            onNotify('success', 'Ticket cerrado y archivado correctamente');
            return true;
        } catch (error: any) {
            console.error('Error archiving ticket:', error);
            onNotify('error', 'Error al archivar ticket: ' + error.message);
            return false;
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Add a comment to ticket timeline
     */
    const addComment = async (ticketId: string, comment: string): Promise<boolean> => {
        if (!currentUser || !comment.trim()) return false;

        try {
            const { error } = await supabase
                .from('timeline_events')
                .insert({
                    ticket_id: ticketId,
                    user_id: currentUser.id,
                    user_name: currentUser.name,
                    event_type: 'COMMENT',
                    content: comment,
                });

            if (error) throw error;

            await onReload();
            return true;
        } catch (error: any) {
            console.error('Error adding comment:', error);
            onNotify('error', 'Error al agregar comentario: ' + error.message);
            return false;
        }
    };

    return {
        createTicket,
        updateTicket,
        deleteTicket,
        addComment,
        archiveTicket,
        isLoading
    };
};
